
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { X, Sparkles } from 'lucide-react';
import { MarkdownEditor } from '@/components/ui/markdown-editor';
import { GenerateQuestionAIDialog } from './GenerateQuestionAIDialog';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type { Tables } from '@/integrations/supabase/types';

type Question = Tables<'questions'>;
type QuestionGroup = Tables<'question_groups'>;
type JobPosition = { id: string; role_name: string };

const baseQuestionSchema = z.object({
  question_text: z.string().min(1, 'Question text is required'),
  question_type: z.enum(['mcq', 'open_ended']),
  group_id: z.string().optional(),
  job_position_ids: z.array(z.string()).optional(),
  is_must_have: z.boolean().default(false),
  is_enabled: z.boolean().default(true),
  score: z.number().min(1, 'Score must be at least 1'),
});

const mcqSchema = baseQuestionSchema.extend({
  question_type: z.literal('mcq'),
  option_a: z.string().min(1, 'Option A is required'),
  option_b: z.string().min(1, 'Option B is required'),
  option_c: z.string().min(1, 'Option C is required'),
  option_d: z.string().min(1, 'Option D is required'),
  correct_answer: z.enum(['A', 'B', 'C', 'D']),
  ai_evaluation_criteria: z.string().optional(),
});

const openEndedSchema = baseQuestionSchema.extend({
  question_type: z.literal('open_ended'),
  ai_evaluation_criteria: z.string().min(1, 'AI evaluation criteria is required'),
  option_a: z.string().optional(),
  option_b: z.string().optional(),
  option_c: z.string().optional(),
  option_d: z.string().optional(),
  correct_answer: z.enum(['A', 'B', 'C', 'D']).optional(),
});

const questionSchema = z.discriminatedUnion('question_type', [
  mcqSchema,
  openEndedSchema,
]);

type QuestionForm = z.infer<typeof questionSchema>;

interface QuestionDialogProps {
  open: boolean;
  onClose: () => void;
  editingQuestion: Question | null;
  questionGroups: QuestionGroup[];
  jobPositions: JobPosition[];
  onSuccess: () => void;
}

const defaultFormValues: QuestionForm = {
  question_text: '',
  question_type: 'mcq',
  group_id: undefined,
  job_position_ids: [],
  is_must_have: false,
  is_enabled: true,
  score: 1,
  option_a: '',
  option_b: '',
  option_c: '',
  option_d: '',
  correct_answer: 'A',
  ai_evaluation_criteria: '',
};

export function QuestionDialog({
  open,
  onClose,
  editingQuestion,
  questionGroups,
  jobPositions,
  onSuccess,
}: QuestionDialogProps) {
  const [loading, setLoading] = useState(false);
  const [selectedJobPositions, setSelectedJobPositions] = useState<string[]>([]);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const { toast } = useToast();

  const form = useForm<QuestionForm>({
    resolver: zodResolver(questionSchema),
    defaultValues: defaultFormValues,
  });

  useEffect(() => {
    if (!editingQuestion && questionGroups.length === 1) {
      form.setValue('group_id', questionGroups[0].id);
    }
  }, [editingQuestion, questionGroups, form]);

  const questionType = form.watch('question_type');
  const groupId = form.watch('group_id');
  const selectedGroup = questionGroups.find(g => g.id === groupId);

  const resetForm = useCallback(() => {
    setSelectedJobPositions([]);
    form.reset(defaultFormValues);
  }, [form]);

  useEffect(() => {
    if (editingQuestion) {
      const options = editingQuestion.options as Record<string, string> | null;
      const jobPositionIds = editingQuestion.job_position_ids || [];

      setSelectedJobPositions(jobPositionIds);

      form.reset({
        question_text: editingQuestion.question_text,
        question_type: editingQuestion.question_type as 'mcq' | 'open_ended',
        group_id: editingQuestion.group_id || undefined,
        job_position_ids: jobPositionIds,
        is_must_have: editingQuestion.is_must_have,
        is_enabled: editingQuestion.is_enabled ?? true,
        score: editingQuestion.score,
        ...(editingQuestion.question_type === 'mcq' && options
          ? {
            option_a: options.A || '',
            option_b: options.B || '',
            option_c: options.C || '',
            option_d: options.D || '',
            correct_answer: editingQuestion.correct_answer as 'A' | 'B' | 'C' | 'D',
          }
          : {}),
        ai_evaluation_criteria: editingQuestion.ai_evaluation_criteria || '',
      });
    } else {
      resetForm();
    }
  }, [editingQuestion, form, resetForm]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open && !editingQuestion) {
      resetForm();
    }
  }, [open, editingQuestion, resetForm]);

  const handleJobPositionSelect = (positionId: string) => {
    if (positionId === 'none') return;

    if (!selectedJobPositions.includes(positionId)) {
      const newSelected = [...selectedJobPositions, positionId];
      setSelectedJobPositions(newSelected);
      form.setValue('job_position_ids', newSelected);
    }
  };

  const removeJobPosition = (positionId: string) => {
    const newSelected = selectedJobPositions.filter(id => id !== positionId);
    setSelectedJobPositions(newSelected);
    form.setValue('job_position_ids', newSelected);
  };

  const handleAIGenerated = (data: any) => {
    if (!data) return;
    if (data.question) {
      form.setValue('question_text', data.question);
    }
    if (data.ai_prompt) {
      form.setValue('question_type', 'open_ended');
      form.setValue('ai_evaluation_criteria', data.ai_prompt);
    }
    if (data.options) {
      form.setValue('question_type', 'mcq');
      form.setValue('option_a', data.options.A || '');
      form.setValue('option_b', data.options.B || '');
      form.setValue('option_c', data.options.C || '');
      form.setValue('option_d', data.options.D || '');
    }
    if (data.correct_answer) {
      form.setValue('correct_answer', data.correct_answer as 'A' | 'B' | 'C' | 'D');
    }
  };

  const onSubmit = async (data: QuestionForm) => {
    setLoading(true);

    try {
      const questionData: any = {
        question_text: data.question_text,
        question_type: data.question_type,
        group_id: data.group_id === 'none' ? null : data.group_id || null,
        job_position_ids: selectedJobPositions.length > 0 ? selectedJobPositions : [],
        is_must_have: data.is_must_have,
        is_enabled: data.is_enabled,
        score: data.score,
      };

      if (data.question_type === 'mcq') {
        questionData.options = {
          A: data.option_a,
          B: data.option_b,
          C: data.option_c,
          D: data.option_d,
        };
        questionData.correct_answer = data.correct_answer;
        questionData.ai_evaluation_criteria = null;
      } else {
        questionData.options = null;
        questionData.correct_answer = null;
        questionData.ai_evaluation_criteria = data.ai_evaluation_criteria;
      }

      if (editingQuestion) {
        const { error } = await supabase
          .from('questions')
          .update(questionData)
          .eq('id', editingQuestion.id);

        if (error) throw error;

        toast({
          title: 'Success',
          description: 'Question updated successfully',
        });
      } else {
        const { error } = await supabase
          .from('questions')
          .insert(questionData);

        if (error) throw error;

        toast({
          title: 'Success',
          description: 'Question created successfully',
        });
      }

      onSuccess();

      // Clear form data after successful submission
      resetForm();

      onClose();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save question',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editingQuestion ? 'Edit Question' : 'Create Question'}
          </DialogTitle>
          <DialogDescription>
            Create questions for your assessment. Choose between MCQ and open-ended questions.
          </DialogDescription>
          {!editingQuestion && (
            <Button type="button" variant="outline" size="sm" onClick={() => setAiDialogOpen(true)} className="mt-2 self-start flex items-center gap-1">
              <Sparkles className="h-4 w-4" />
              Create with AI
            </Button>
          )}
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="question_text"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Question Text</FormLabel>
                  <FormControl>
                    <MarkdownEditor
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Enter your question here using markdown..."
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="question_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Question Type</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="mcq">Multiple Choice</SelectItem>
                        <SelectItem value="open_ended">Open Ended</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="score"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Score</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        value={field.value}
                        onChange={(e) => field.onChange(parseInt(e.target.value) || 1)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 gap-4">
              {!(questionGroups.length === 1 && !editingQuestion) && (
                <FormField
                  control={form.control}
                  name="group_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Question Group (Optional)</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value || 'none'}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select group" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">No group</SelectItem>
                          {questionGroups.map((group) => (
                            <SelectItem key={group.id} value={group.id}>
                              {group.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <div className="space-y-2">
                <FormLabel>Job Positions (Optional)</FormLabel>
                <Select onValueChange={handleJobPositionSelect}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select positions" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No specific position</SelectItem>
                    {jobPositions
                      .filter(position => !selectedJobPositions.includes(position.id))
                      .map((position) => (
                        <SelectItem key={position.id} value={position.id}>
                          {position.role_name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>

                {selectedJobPositions.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {selectedJobPositions.map((positionId) => {
                      const position = jobPositions.find(p => p.id === positionId);
                      return position ? (
                        <Badge key={positionId} variant="secondary" className="flex items-center gap-1">
                          {position.role_name}
                          <X
                            className="h-3 w-3 cursor-pointer"
                            onClick={() => removeJobPosition(positionId)}
                          />
                        </Badge>
                      ) : null;
                    })}
                  </div>
                )}
              </div>
            </div>

            <FormField
              control={form.control}
              name="is_must_have"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>
                      Must-have question
                    </FormLabel>
                    <FormDescription>
                      This question will always be included in tests
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_enabled"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Enabled</FormLabel>
                    <FormDescription>
                      Disable to exclude this question from assessments
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />

            {questionType === 'mcq' && (
              <>
                <div className="space-y-4">
                  <h4 className="font-medium">Answer Options</h4>
                  <div className="grid grid-cols-1 gap-3">
                    {['A', 'B', 'C', 'D'].map((letter) => (
                      <FormField
                        key={letter}
                        control={form.control}
                        name={`option_${letter.toLowerCase()}` as keyof QuestionForm}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Option {letter}</FormLabel>
                            <FormControl>
                              <MarkdownEditor
                                value={field.value as string || ''}
                                onChange={field.onChange}
                                placeholder={`Enter option ${letter} using markdown...`}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    ))}
                  </div>
                </div>

                <FormField
                  control={form.control}
                  name="correct_answer"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Correct Answer</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select correct answer" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="A">Option A</SelectItem>
                          <SelectItem value="B">Option B</SelectItem>
                          <SelectItem value="C">Option C</SelectItem>
                          <SelectItem value="D">Option D</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}

            {questionType === 'open_ended' && (
              <FormField
                control={form.control}
                name="ai_evaluation_criteria"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>AI Evaluation Criteria</FormLabel>
                    <FormControl>
                      <MarkdownEditor
                        value={field.value || ''}
                        onChange={field.onChange}
                        placeholder="Describe what makes a good answer to this question using markdown..."
                      />
                    </FormControl>
                    <FormDescription>
                      Provide detailed criteria for AI to evaluate open-ended responses
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Saving...' : editingQuestion ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
      <GenerateQuestionAIDialog
        open={aiDialogOpen}
        onClose={() => setAiDialogOpen(false)}
        questionType={questionType as 'mcq' | 'open_ended'}
        group={selectedGroup ? { name: selectedGroup.name, description: selectedGroup.description || null } : undefined}
        onGenerated={handleAIGenerated}
      />
    </Dialog>

  );
}
